import React, { useRef, useState } from 'react';
import { Button, Input, Spin, Table, Tag } from 'antd';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ReportFilter, ReportItem } from '../../services/c322Api';
import { useC322Report } from './hooks/useC322Report';

const C322Report: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const { loading, reportData, queried, noData, runQuery } = useC322Report();

  // Filter state
  const [customerStart, setCustomerStart] = useState('');
  const [customerEnd, setCustomerEnd] = useState('');
  const [scStart, setScStart] = useState('');
  const [scEnd, setScEnd] = useState('');

  const handleQuery = () => {
    const filter: ReportFilter = {
      customer_start: customerStart || undefined,
      customer_end: customerEnd || undefined,
      sc_start: scStart || undefined,
      sc_end: scEnd || undefined,
    };
    runQuery(filter);
  };

  const handlePrint = () => {
    if (!printRef.current || !reportData) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const now = new Date().toLocaleString('zh-TW');
    win.document.write(`<!DOCTYPE html><html><head><title>訂單跟催表</title>
<style>
  body { font-family: 'Microsoft JhengHei', sans-serif; margin: 20px; font-size: 11px; }
  h2 { text-align: center; margin-bottom: 4px; }
  .meta { text-align: center; font-size: 10px; color: #555; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #333; padding: 2px 5px; }
  th { background: #f0f0f0; text-align: center; }
  .right { text-align: right; }
  .center { text-align: center; }
  @media print { body { margin: 8mm; } }
</style></head><body>
<h2>訂單跟催表</h2>
<div class="meta">列印時間: ${now}　　共 ${reportData.total_count} 筆</div>
<table>
<thead>
  <tr>
    <th>S/C No</th><th>日期</th><th>客戶</th><th>品名</th>
    <th>色號</th><th>顏色名</th><th style="text-align:right">色量</th>
    <th>交貨日</th><th>付款條件</th><th>備註</th>
    <th style="text-align:right">主套量</th><th style="text-align:right">副套量</th>
  </tr>
</thead>
<tbody>
${reportData.items.map(r => `<tr>
  <td class="center">${r.fc110_01}</td>
  <td class="center">${r.fc110_11 || ''}</td>
  <td class="center">${r.fc110_12}</td>
  <td>${r.fc111_11}</td>
  <td class="center">${r.fc115_11}</td>
  <td>${r.fc115_12}${r.fc115_13 ? ' ' + r.fc115_13 : ''}</td>
  <td class="right">${r.fc115_14.toFixed(2)}</td>
  <td class="center">${r.fc111_12 || ''}</td>
  <td>${r.fc110_221 || r.fc110_22}</td>
  <td>${r.fc111_291 || r.fc111_29}</td>
  <td class="right">${r.fc111_151.toFixed(2)}</td>
  <td class="right">${r.fc111_152.toFixed(2)}</td>
</tr>`).join('')}
</tbody>
</table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  // Table columns
  const columns: ColumnsType<ReportItem> = [
    { title: 'S/C No', dataIndex: 'fc110_01', key: 'fc110_01', align: 'center', width: 90, fixed: 'left' },
    { title: '日期', dataIndex: 'fc110_11', key: 'fc110_11', align: 'center', width: 90,
      render: (v: string) => v ? v.substring(0, 10) : '' },
    { title: '客戶', dataIndex: 'fc110_12', key: 'fc110_12', align: 'center', width: 70 },
    { title: '客戶訂單', dataIndex: 'fc110_14', key: 'fc110_14', ellipsis: true, width: 100 },
    { title: '品名', dataIndex: 'fc111_11', key: 'fc111_11', ellipsis: true, width: 120 },
    { title: '規格', dataIndex: 'fc111_21', key: 'fc111_21', ellipsis: true, width: 100 },
    { title: '客戶品號', dataIndex: 'fc111_55', key: 'fc111_55', ellipsis: true, width: 90 },
    { title: '色號', dataIndex: 'fc115_11', key: 'fc115_11', align: 'center', width: 60 },
    { title: '顏色名1', dataIndex: 'fc115_12', key: 'fc115_12', ellipsis: true, width: 90 },
    { title: '顏色名2', dataIndex: 'fc115_13', key: 'fc115_13', ellipsis: true, width: 90 },
    {
      title: '色量', dataIndex: 'fc115_14', key: 'fc115_14', align: 'right', width: 80,
      render: (v: number) => v.toFixed(2),
    },
    { title: '單位', dataIndex: 'fc111_14', key: 'fc111_14', align: 'center', width: 55 },
    { title: '交貨日', dataIndex: 'fc111_12', key: 'fc111_12', align: 'center', width: 90,
      render: (v: string) => v ? v.substring(0, 10) : '' },
    { title: '付款條件', dataIndex: 'fc110_221', key: 'fc110_221', ellipsis: true, width: 100,
      render: (v: string, r: ReportItem) => v || r.fc110_22 },
    { title: '備註說明', dataIndex: 'fc111_291', key: 'fc111_291', ellipsis: true, width: 100,
      render: (v: string, r: ReportItem) => v || r.fc111_29 },
    {
      title: '主套量', dataIndex: 'fc111_151', key: 'fc111_151', align: 'right', width: 80,
      render: (v: number) => v > 0 ? v.toFixed(2) : '',
    },
    {
      title: '副套量', dataIndex: 'fc111_152', key: 'fc111_152', align: 'right', width: 80,
      render: (v: number) => v > 0 ? v.toFixed(2) : '',
    },
  ];

  const rowKey = (r: ReportItem) => `${r.fc110_01}-${r.fc111_02}-${r.fc115_03}`;

  return (
    <Spin spinning={loading}>
      <div className="max-w-screen-2xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">訂單跟催表 (pwc322f1)</h2>

        {/* Filter Panel */}
        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-20">客戶代號</span>
            <Input
              value={customerStart}
              onChange={e => setCustomerStart(e.target.value)}
              placeholder="起"
              style={{ width: 100 }}
              maxLength={6}
            />
            <span className="text-sm">~</span>
            <Input
              value={customerEnd}
              onChange={e => setCustomerEnd(e.target.value)}
              placeholder="迄"
              style={{ width: 100 }}
              maxLength={6}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-sm font-medium w-20">S/C 號碼</span>
            <Input
              value={scStart}
              onChange={e => setScStart(e.target.value)}
              placeholder="起"
              style={{ width: 120 }}
              maxLength={10}
            />
            <span className="text-sm">~</span>
            <Input
              value={scEnd}
              onChange={e => setScEnd(e.target.value)}
              placeholder="迄"
              style={{ width: 120 }}
              maxLength={10}
            />
          </div>
          <div className="flex gap-2">
            <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery}>查詢</Button>
            {reportData && (
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印預覽</Button>
            )}
          </div>
        </div>

        {/* Results */}
        {queried && !noData && reportData && (
          <div ref={printRef}>
            <div className="flex gap-4 mb-3">
              <Tag color="blue">共 {reportData.total_count} 筆</Tag>
            </div>
            <Table<ReportItem>
              dataSource={reportData.items}
              columns={columns}
              rowKey={rowKey}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
              scroll={{ x: 1400 }}
              bordered
            />
          </div>
        )}

        {queried && noData && (
          <div className="text-center text-gray-400 py-8">查無符合條件的訂單資料!</div>
        )}
      </div>
    </Spin>
  );
};

export default C322Report;
